import React from 'react';
import { Typography } from '@duxton-web/ui';
import _ from 'lodash';

import { error } from '../logging';
import { formatList } from '../string';
import { CONCEDO_REQUEST_URL } from '../url';
import {
  getPermissionConfig,
  IgnoredRoles,
  Permission,
  RoleNameToValue,
  UserAction,
  UserActionConfig,
} from './config';
import { normalizeLegacyRoleName } from './roles';
import { RoleName } from '@/common/const/Auth';
import { REQUESTED } from '@/constants';
import { Criticality, UserRoleScopeMapping } from '@/lib/App';

export { normalizeLegacyRoleName } from './roles';

enum ErrorCode {
  MULTIPLE_SCOPES_REQUIRED = 'MULTIPLE_SCOPES_REQUIRED',
  SINGLE_SCOPE_REQUIRED = 'SINGLE_SCOPE_REQUIRED',
  SCOPE_NOT_REQUIRED = 'SCOPE_NOT_REQUIRED',
}

type MultipleScopesError = {
  hasAccess: false;
  errorCode: ErrorCode.MULTIPLE_SCOPES_REQUIRED;
  errorDetails: {
    scopes: string[];
    roleRequired: string;
  };
};

type SingleScopeError = {
  hasAccess: false;
  errorCode: ErrorCode.SINGLE_SCOPE_REQUIRED;
  errorDetails: {
    scope: string;
    roleRequired: string;
    roleUserHas: string;
  };
};

type ScopeNotRequiredError = {
  hasAccess: false;
  errorCode: ErrorCode.SCOPE_NOT_REQUIRED;
  errorDetails: {
    roleRequired: string;
    roleUserHas: string;
  };
};

type ErrorResult =
  | MultipleScopesError
  | SingleScopeError
  | ScopeNotRequiredError;

type SuccessResult = {
  hasAccess: true;
};

type AccessCheckResult = SuccessResult | ErrorResult;

export type AccessTarget = {
  service?: string | null;
  techFamily?: string | null;
  criticality?: Criticality | null;
};

export type AccessTargetInput = AccessTarget | null | undefined;

export type TechFamilyScopeAliasMap = Record<string, string[]>;

type VariableLike = {
  service?: string | null;
  backend?: string | null;
  techFamily?: string | null;
  criticality?: Criticality | null;
};

export const ROLLOUT_CRITICAL_PUBLISHER_POLICY = 'rollout-critical-publisher';
export const ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY =
  'rollout-non-critical-publisher';
export const EXPERIMENT_PUBLISHER_POLICY = 'experiment-publisher';

const servicePolicyLabels: Record<string, string> = {
  [EXPERIMENT_PUBLISHER_POLICY]: 'Experiment Publisher Role',
  [ROLLOUT_CRITICAL_PUBLISHER_POLICY]: 'Rollout Critical Publisher Role',
  [ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY]:
    'Rollout Non-Critical Publisher Role',
};

const adminEquivalentPolicies = new Set([
  'admin',
  'tech-support',
  'techsupport',
  'service-account',
  'serviceaccount',
]);

const defaultGrantedActions = new Set<UserAction>([
  UserAction.CREATE_A_VARIABLE,
  UserAction.CREATE_VARIABLE_USING_CURRENT_TF,
  UserAction.CREATE_AN_EXPERIMENT,
  UserAction.CLONE_CURRENT_EXPERIMENT,
  UserAction.CREATE_EXPERIMENT_USING_CURRENT_VARIABLE,
  UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.CREATE_GROUP,
]);

const requestedStateDefaultGrantedActions = new Set<UserAction>([
  UserAction.EDIT_CURRENT_EXPERIMENT,
  UserAction.EDIT_EXPERIMENT_USING_CURRENT_VARIABLE,
]);

export const rolloutPublisherActions = new Set<UserAction>([
  UserAction.EDIT_CURRENT_VARIABLE,
  UserAction.EDIT_CURRENT_VARIABLE_USING_CURRENT_TF,
  UserAction.ARCHIVE_CURRENT_VARIABLE,
  UserAction.UNDO_ARCHIVE_CURRENT_VARIABLE,
  UserAction.APPROVE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.EDIT_GROUP,
]);

export const experimentPublisherActions = new Set<UserAction>([
  UserAction.EDIT_CURRENT_VARIABLE,
  UserAction.EDIT_CURRENT_VARIABLE_USING_CURRENT_TF,
  UserAction.EDIT_CURRENT_EXPERIMENT,
  UserAction.CREATE_EXPERIMENT_USING_CURRENT_VARIABLE,
  UserAction.EDIT_EXPERIMENT_USING_CURRENT_VARIABLE,
  UserAction.ABORT_CURRENT_EXPERIMENT,
  UserAction.PAUSE_CURRENT_EXPERIMENT,
  UserAction.APPROVE_CURRENT_EXPERIMENT,
  UserAction.REJECT_CURRENT_EXPERIMENT,
  UserAction.EDIT_GROUP,
]);

export const normalizeScope = (value?: string | null): string =>
  value?.trim().toLowerCase() ?? '';

export const normalizePolicyName = (policyName?: string | null): string => {
  const normalized = policyName?.trim().toLowerCase().replace(/_/g, '-') ?? '';

  if (normalized === 'experiment') {
    return EXPERIMENT_PUBLISHER_POLICY;
  }

  if (normalized === 'rollout-critical') {
    return ROLLOUT_CRITICAL_PUBLISHER_POLICY;
  }

  if (normalized === 'rollout-non-critical') {
    return ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY;
  }

  return normalized;
};

const uniqueNonEmpty = (values: string[]): string[] => {
  return _.uniq(values.filter((value) => value?.trim() !== ''));
};

export const uniqueNormalizedScopes = (
  values: Array<string | null | undefined>,
): string[] => {
  return _.uniq(values.map(normalizeScope).filter(Boolean));
};

export const buildTechFamilyScopeAliasMap = (
  aliasGroups: Array<Array<string | null | undefined>>,
): TechFamilyScopeAliasMap => {
  const aliasMap: TechFamilyScopeAliasMap = {};

  aliasGroups.forEach((aliasGroup) => {
    const aliases = uniqueNormalizedScopes(aliasGroup);

    aliases.forEach((alias) => {
      aliasMap[alias] = _.uniq([...(aliasMap[alias] ?? []), ...aliases]);
    });
  });

  return aliasMap;
};

const formatAlternativeList = (list: string[]): string => {
  if (list.length <= 1) return list[0] ?? '';

  return `${list.slice(0, -1).join(', ')} or ${list.slice(-1)[0]}`;
};

export const buildAccessTargetFromVariable = (
  variable?: VariableLike | null,
): AccessTarget | null => {
  if (!variable) return null;

  return {
    service: variable.service ?? variable.backend,
    techFamily: variable.techFamily,
    criticality: variable.criticality,
  };
};

export const buildAccessTargetsFromVariables = (
  variables?: Array<VariableLike | null | undefined> | null,
): AccessTarget[] => {
  return _.uniqBy(
    (variables ?? [])
      .map(buildAccessTargetFromVariable)
      .filter((target): target is AccessTarget => Boolean(target))
      .filter((target) => {
        return (
          Boolean(target.service?.trim()) ||
          Boolean(target.techFamily?.trim()) ||
          target.criticality !== undefined
        );
      }),
    (target) =>
      [
        normalizeScope(target.service),
        normalizeScope(target.techFamily),
        target.criticality ?? '',
      ].join(':'),
  );
};

export class PermissionUtils {
  private userPermissions: Permission[];

  private userRoleScopes: UserRoleScopeMapping[];

  private isConcedoServiceAuthZEnabled: boolean;

  private techFamilyScopeAliases: TechFamilyScopeAliasMap;

  constructor(
    userPermissions: Permission[],
    userRoleScopes: UserRoleScopeMapping[] = [],
    isConcedoServiceAuthZEnabled = false,
    techFamilyScopeAliases: TechFamilyScopeAliasMap = {},
  ) {
    this.userPermissions = (userPermissions ?? [])
      .filter(Boolean)
      .map((permission) => ({
        ...permission,
        role: normalizeLegacyRoleName(permission?.role, {
          techFamily: permission?.tf,
        }),
      }));
    this.userRoleScopes = (userRoleScopes ?? [])
      .filter(Boolean)
      .map((roleScope) => {
        const policyNames = Array.isArray(roleScope?.policyNames)
          ? roleScope.policyNames
          : [];

        return {
          ...roleScope,
          roleName: normalizeLegacyRoleName(roleScope?.roleName, {
            techFamily: roleScope?.tf,
            policyNames,
          }),
        };
      });
    this.isConcedoServiceAuthZEnabled = isConcedoServiceAuthZEnabled;
    this.techFamilyScopeAliases = buildTechFamilyScopeAliasMap(
      Object.entries(techFamilyScopeAliases ?? {}).map(([scope, aliases]) => [
        scope,
        ...(Array.isArray(aliases) ? aliases : []),
      ]),
    );
  }

  _process(action: UserAction, _scopes?: string[]): AccessCheckResult {
    const scopes = _.uniq(
      (_scopes || []).filter((scope) => scope?.trim() !== ''),
    );
    if (this.userPermissions.some(({ role }) => IgnoredRoles.includes(role))) {
      return {
        hasAccess: true,
      };
    }

    const { role, scopeRequired } = getPermissionConfig(action);

    if (scopeRequired && !scopes.length) {
      // Unexpected error, should not happen
      error(
        new Error('Scope is required for this action'),
        `details: ${JSON.stringify({ action, scopes })}`,
      );
      return {
        hasAccess: true,
      };
    }

    const potentialErrorCode: ErrorCode =
      scopes?.length > 1
        ? ErrorCode.MULTIPLE_SCOPES_REQUIRED
        : scopes?.length === 0
          ? ErrorCode.SCOPE_NOT_REQUIRED
          : ErrorCode.SINGLE_SCOPE_REQUIRED;

    switch (potentialErrorCode) {
      case ErrorCode.SCOPE_NOT_REQUIRED: {
        const highestRole =
          _.maxBy(this.userPermissions, ({ role }) => RoleNameToValue[role])
            ?.role || RoleName.VIEWER;

        if (
          highestRole &&
          RoleNameToValue[highestRole] >= RoleNameToValue[role]
        ) {
          return {
            hasAccess: true,
          };
        }

        return {
          hasAccess: false,
          errorCode: potentialErrorCode,
          errorDetails: {
            roleRequired: role,
            roleUserHas: highestRole,
          },
        };
      }

      case ErrorCode.SINGLE_SCOPE_REQUIRED: {
        const scope = scopes[0];
        const highestRoleInScope =
          _.maxBy(
            this.userPermissions.filter(({ tf }) =>
              this.techFamilyScopeMatches(scope, tf),
            ),
            ({ role }) => RoleNameToValue[role],
          )?.role || RoleName.VIEWER;

        if (
          highestRoleInScope &&
          RoleNameToValue[highestRoleInScope] >= RoleNameToValue[role]
        ) {
          return {
            hasAccess: true,
          };
        }

        return {
          hasAccess: false,
          errorCode: potentialErrorCode,
          errorDetails: {
            scope,
            roleRequired: role,
            roleUserHas: highestRoleInScope,
          },
        };
      }

      case ErrorCode.MULTIPLE_SCOPES_REQUIRED: {
        const hasAccess = scopes.every((scope) => {
          return this.userPermissions.some(
            ({ role: userRole, tf: userScope }) => {
              return (
                this.techFamilyScopeMatches(scope, userScope) &&
                RoleNameToValue[userRole] >= RoleNameToValue[role]
              );
            },
          );
        });

        return hasAccess
          ? {
              hasAccess,
            }
          : {
              hasAccess: false,
              errorCode: potentialErrorCode,
              errorDetails: {
                scopes,
                roleRequired: role,
              },
            };
      }

      default:
        throw new Error(`Unhandled error code: ${potentialErrorCode}`);
    }
  }

  // Check if user has the required permission to perform an action
  canPerformAction(
    action: UserAction,
    scopes?: string[],
    options?: {
      accessTargets?: AccessTargetInput[];
      resourceState?: string;
    },
  ): boolean {
    return this.process(action, scopes, options).hasAccess;
  }

  private process(
    action: UserAction,
    scopes?: string[],
    options?: {
      accessTargets?: AccessTargetInput[];
      resourceState?: string;
    },
  ): AccessCheckResult {
    if (!this.isConcedoServiceAuthZEnabled) {
      return this._process(action, scopes);
    }

    if (this.hasConcedoAccess(action, scopes, options)) {
      return { hasAccess: true };
    }

    const { role, scopeRequired } = getPermissionConfig(action);
    if (scopeRequired && !uniqueNonEmpty(scopes ?? []).length) {
      return {
        hasAccess: false,
        errorCode: ErrorCode.SINGLE_SCOPE_REQUIRED,
        errorDetails: {
          scope: '',
          roleRequired: role,
          roleUserHas: RoleName.VIEWER,
        },
      };
    }

    return this._process(action, scopes);
  }

  private hasConcedoAccess(
    action: UserAction,
    techFamilies?: string[],
    options?: {
      accessTargets?: AccessTargetInput[];
      resourceState?: string;
    },
  ): boolean {
    if (this.isDefaultAccessGranted(action, options?.resourceState)) {
      return true;
    }

    if (this.hasAdminEquivalentPolicy()) {
      return true;
    }

    const requiresRolloutPolicy = rolloutPublisherActions.has(action);
    const requiresExperimentPolicy = experimentPublisherActions.has(action);

    if (!requiresRolloutPolicy && !requiresExperimentPolicy) {
      return false;
    }

    const targets = this.getAccessTargets(options?.accessTargets, techFamilies);

    if (!targets.length) {
      return this.hasMatchingConcedoRole(
        this.getAllowedConcedoPolicies(action, null),
        null,
      );
    }

    return targets.every((target) => {
      return this.hasMatchingConcedoRole(
        this.getAllowedConcedoPolicies(action, target),
        target,
      );
    });
  }

  private isDefaultAccessGranted(
    action: UserAction,
    resourceState?: string,
  ): boolean {
    if (defaultGrantedActions.has(action)) {
      return true;
    }

    return (
      requestedStateDefaultGrantedActions.has(action) &&
      normalizeScope(resourceState) === normalizeScope(REQUESTED)
    );
  }

  private getAccessTargets(
    accessTargets?: AccessTargetInput[],
    techFamilies?: string[],
  ): AccessTarget[] {
    const explicitTargets = buildAccessTargetsFromVariables(accessTargets);
    if (explicitTargets.length) {
      return explicitTargets;
    }

    return uniqueNonEmpty(techFamilies ?? []).map((techFamily) => ({
      techFamily,
    }));
  }

  private getAllowedConcedoPolicies(
    action: UserAction,
    target: AccessTarget | null,
  ): string[] {
    const policies: string[] = [];

    if (experimentPublisherActions.has(action)) {
      policies.push(EXPERIMENT_PUBLISHER_POLICY);
    }

    if (rolloutPublisherActions.has(action)) {
      policies.push(...this.getAllowedRolloutPolicies(action, target));
    }

    return uniqueNonEmpty(policies);
  }

  private getAllowedRolloutPolicies(
    action: UserAction,
    target: AccessTarget | null,
  ): string[] {
    if (action === UserAction.EDIT_GROUP) {
      return [
        ROLLOUT_CRITICAL_PUBLISHER_POLICY,
        ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY,
      ];
    }

    if (target?.criticality === Criticality.NonCritical) {
      return [
        ROLLOUT_CRITICAL_PUBLISHER_POLICY,
        ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY,
      ];
    }

    return [ROLLOUT_CRITICAL_PUBLISHER_POLICY];
  }

  private hasMatchingConcedoRole(
    allowedPolicies: string[],
    target: AccessTarget | null,
  ): boolean {
    if (!allowedPolicies.length) return false;

    return this.userRoleScopes.some((roleScope) => {
      if (!this.roleScopeMatchesTarget(roleScope, target)) {
        return false;
      }

      return (roleScope?.policyNames ?? [])
        .map(normalizePolicyName)
        .some((policyName) => allowedPolicies.includes(policyName));
    });
  }

  private roleScopeMatchesTarget(
    roleScope: UserRoleScopeMapping,
    target: AccessTarget | null,
  ): boolean {
    if (!target) return true;

    const targetService = normalizeScope(target.service);
    const targetTechFamilies = this.getTargetTechFamilies(target);

    if (!targetService && !targetTechFamilies.length) {
      return false;
    }

    return (
      (targetService && normalizeScope(roleScope?.service) === targetService) ||
      targetTechFamilies.includes(normalizeScope(roleScope?.tf))
    );
  }

  private getTargetTechFamilies(target: AccessTarget): string[] {
    return this.getEquivalentTechFamilyScopes(target.techFamily);
  }

  private getEquivalentTechFamilyScopes(techFamily?: string | null): string[] {
    const scope = normalizeScope(techFamily);

    if (!scope) return [];

    return uniqueNormalizedScopes([
      scope,
      ...(this.techFamilyScopeAliases[scope] ?? []),
    ]);
  }

  private techFamilyScopeMatches(
    targetTechFamily?: string | null,
    userTechFamily?: string | null,
  ): boolean {
    const userScope = normalizeScope(userTechFamily);

    return (
      Boolean(userScope) &&
      this.getEquivalentTechFamilyScopes(targetTechFamily).includes(userScope)
    );
  }

  private hasAdminEquivalentPolicy(): boolean {
    return this.userRoleScopes.some((roleScope) =>
      (roleScope?.policyNames ?? [])
        .map(normalizePolicyName)
        .some((policyName) => adminEquivalentPolicies.has(policyName)),
    );
  }

  private getConcedoRoleRequirementText(
    action: UserAction,
    techFamilies?: string[],
    accessTargets?: AccessTargetInput[],
  ): string {
    if (!this.isConcedoServiceAuthZEnabled) return '';

    const targets = this.getAccessTargets(accessTargets, techFamilies);
    const effectiveTargets = targets.length ? targets : [null];
    const requirements = effectiveTargets
      .map((target) => {
        const policyLabels = uniqueNonEmpty(
          this.getAllowedConcedoPolicies(action, target).map(
            (policyName) => servicePolicyLabels[policyName],
          ),
        );

        if (!policyLabels.length) return '';

        const roleText = formatAlternativeList(policyLabels);
        const service = target?.service?.trim();
        const techFamily = target?.techFamily?.trim();

        if (service) return `${service} ${roleText}`;
        if (techFamily) return `${roleText} for Tech Family: ${techFamily}`;

        return roleText;
      })
      .filter(Boolean);

    return formatAlternativeList(uniqueNonEmpty(requirements));
  }

  generateExplanation({
    action,
    customActionText,
    techFamilies: _techFamilies,
    accessTargets,
    resourceState,
  }: {
    action: UserAction;
    customActionText?: string;
    techFamilies?: string[];
    accessTargets?: AccessTargetInput[];
    resourceState?: string;
  }): [boolean, React.ReactNode] {
    const techFamilies = _.uniq(_techFamilies);
    const result = this.process(action, techFamilies, {
      accessTargets,
      resourceState,
    });

    if (result.hasAccess) {
      return [true, null];
    }

    const { errorCode, errorDetails } = result as ErrorResult;

    const constructActionText = () => {
      return customActionText || getPermissionConfig(action).text;
    };

    const constructRequiredRoleText = () => {
      return (
        <>
          <Typography.Text
            strong
            style={{
              color: 'inherit',
            }}
          >
            {errorDetails.roleRequired}
          </Typography.Text>{' '}
          access
          {errorCode !== ErrorCode.SCOPE_NOT_REQUIRED
            ? ` for Tech Family: ${formatList(techFamilies)}`
            : ''}
        </>
      );
    };

    const concedoRoleRequirementText = this.getConcedoRoleRequirementText(
      action,
      techFamilies,
      accessTargets,
    );
    const concedoRoleRequirementSuffix = concedoRoleRequirementText
      ? ` or ${concedoRoleRequirementText}`
      : '';

    return [
      false,
      // eslint-disable-next-line react/jsx-key
      <span>
        {constructActionText()}
        {' requires '}
        {constructRequiredRoleText()}
        {concedoRoleRequirementSuffix}.
        <br />
        <Typography.Link
          href={CONCEDO_REQUEST_URL}
          target="_blank"
          style={{ color: '#4096ff' }}
        >
          Apply here
        </Typography.Link>
      </span>,
    ];
  }
}

export { UserAction, UserActionConfig };
